/**
 * THE STORY — shared derivation (#767, story-first IA).
 *
 * Corbin's model (threads/story-first-ia/SPEC.md v4): SUMMARY is the ask
 * (office → tech, forward-looking); THE STORY is what happened from that ask
 * (job → office, backward-looking) — high points only, glance-readable beats:
 * "✓ RPZ passed · ✗ DC failed (relief valve) → $172 invoice · $2,340 quote
 * awaiting approval". Derived, never typed.
 *
 * This is the shared service: the job page's NOW panel consumes the full
 * per-device derivation (`deriveJobStory`), and the closeout context band
 * (close-out/bandModel.ts) shares the composition rule (`composeStoryLine`)
 * over its coarser queue read model. Pure functions over data the pages
 * already fetch — no endpoints, no mutations.
 */

import { assemblyShort } from "./vocabulary";
import { testState } from "./test-state";

// ── Inputs (structural — mirror the job page's response shapes) ────────────

export interface StoryJobLike {
    status: string;
    scheduled_start?: string | null;
    assigned_to_name?: string | null;
}

export interface StoryTestLike {
    asset_id: string;
    asset_name: string;
    size_label?: string;
    assembly_type?: string;
    overall_result: string;
    performed_at: string;
    failure_reasons?: string[] | null;
    performed_by_name?: string | null;
    tester_name?: string | null;
}

export interface StoryInvoiceLike {
    id: string;
    status: string;
    total_amount?: number | string | null;
    st_balance?: number | string | null;
    payments_total?: number | string | null;
    /** Successful sends of this invoice on the ledger (jobs.ts #1142). The
     *  status enum has no 'sent': an emailed invoice stays 'open' until paid,
     *  so this is the only way to tell "sent" from "finalized, never sent". */
    emailed_count?: number | null;
}

export interface StoryEstimateLike {
    id: string;
    display_number?: string | null;
    status: string;
    subtotal?: number | string | null;
}

// ── Output ─────────────────────────────────────────────────────────────────

/** What happened to one device. `not_tested` is a device the tech couldn't
 *  test (no key, no access) — its row is `incomplete`, never a pass. */
export type StoryOutcome = "passed" | "failed" | "not_tested" | "not_known";

export interface StoryBeat {
    assetId: string;
    /** Short device label a human scans — `1" RPZ`, `¾" DC`. */
    label: string;
    /** True only for a real pass. A couldn't-test device is NOT passed. */
    passed: boolean;
    outcome: StoryOutcome;
    /** Short "why" on a fail — first failure reason, humanized. */
    reason: string | null;
}

export interface StoryMoney {
    kind: "invoice" | "estimate";
    id: string;
    /** "Invoice" / "Estimate #E-1042" — the row's display name. */
    label: string;
    /** "$172" — null when the amount isn't known. */
    amount: string | null;
    /** Plain-English status word: "draft", "awaiting approval", "paid", … */
    status: string;
}

export interface JobStory {
    /** True once there are test results to tell a story from. */
    happened: boolean;
    /** "Aug 6 · Brian Jr" — when + who, from the latest result. */
    head: string | null;
    beats: StoryBeat[];
    money: StoryMoney[];
    /** The whole story as one compact line (closeout-band composition). */
    line: string | null;
    /** What the story row says before anything happened. */
    preVisitLine: string | null;
    /** "Sent via FWISD vendor portal on Oct 7 by Darcy" when the office
     *  recorded the packet as delivered without email (2026-10-07). */
    delivery?: string | null;
}

/** A delivery the office recorded without email, as the job page reads it. */
export interface StoryOtherWayLike {
    channel: string;
    label: string | null;
    sentAt: string;
    sentByName: string | null;
}

/** "Sent via FWISD vendor portal on Oct 7 by Darcy". */
export function otherWayStory(d: StoryOtherWayLike): string {
    const l = d.label?.trim() || null;
    const how =
        d.channel === "portal"
            ? `via ${l ?? "the customer's portal"}`
            : d.channel === "mail"
              ? l
                  ? `by mail (${l})`
                  : "by mail"
              : d.channel === "in_person"
                ? l
                    ? `in person (${l})`
                    : "in person"
                : l
                  ? `via ${l}`
                  : "another way";
    const day = fmtDay(d.sentAt);
    const who = d.sentByName ? d.sentByName.split(" ")[0] : null;
    return `Sent ${how}${day ? ` on ${day}` : ""}${who ? ` by ${who}` : ""}`;
}

export interface MoneyTotals {
    /** Sum of live invoice totals (cancelled/void excluded). */
    invoiceTotal: number;
    /** What's still uncollected on those invoices. */
    invoiceOutstanding: number;
    /** Value of estimates still waiting on a yes (draft/sent). */
    estimateAwaiting: number;
    /** Everything not yet collected: outstanding invoices + awaiting quotes. */
    balance: number;
}

// ── The shared composition rule ────────────────────────────────────────────

/**
 * One line, one convention, both surfaces: narrative parts joined with " — ",
 * and the money bits appended behind the "→" arrow — the causality Corbin
 * asked for (the visit and its failures are WHY these amounts exist).
 */
export function composeStoryLine(parts: string[], moneyBits: string[]): string {
    const all = parts.filter((p) => p.length > 0);
    if (moneyBits.length > 0) all.push(`→ ${moneyBits.join(" + ")}`);
    return all.join(" — ");
}

// ── Small formatters ───────────────────────────────────────────────────────

function fmtMoney(raw: number | string | null | undefined): string | null {
    if (raw == null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return `$${Math.round(n).toLocaleString("en-US")}`;
}

function fmtDay(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtDateTime(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** "relief_valve_leaking" → "relief valve leaking"; a sentence passes through
 *  lowercased so it reads as an aside inside parentheses. */
function shortReason(reason: string): string {
    return reason
        .replace(/_/g, " ")
        .replace(/\.\s*$/, "")
        .trim()
        .toLowerCase();
}

function deviceLabel(t: StoryTestLike): string {
    const code = (t.assembly_type ?? "").trim();
    // assemblyShort comes from the one vocabulary module (#759) — "RPZ",
    // never raw "rpz".
    const short = code ? assemblyShort(code) : null;
    const size = (t.size_label ?? "").trim();
    if (short) return size ? `${size} ${short}` : short;
    return t.asset_name;
}

const INVOICE_STATUS_WORD: Record<string, string> = {
    draft: "draft",
    // 'open' = finalized. Whether it was SENT comes from the ledger
    // (invoiceStatusWord below), not from this map.
    open: "not sent",
    sent: "sent",
    paid: "paid",
    partially_paid: "partially paid",
};

const ESTIMATE_STATUS_WORD: Record<string, string> = {
    draft: "draft",
    sent: "awaiting approval",
    accepted: "approved",
    declined: "declined",
};

const DEAD_MONEY_STATUSES = new Set(["cancelled", "canceled", "void", "voided"]);

function statusWord(map: Record<string, string>, status: string | null | undefined): string {
    const s = (status ?? "").toLowerCase();
    return map[s] ?? s.replace(/_/g, " ");
}

/** "$110 invoice sent" once the ledger shows it went out; a finalized invoice
 *  nobody emailed reads "not sent" (job #092678160 read "draft" after its
 *  packet delivered it, 2026-10-06). */
function invoiceStatusWord(inv: StoryInvoiceLike): string {
    const s = (inv.status ?? "").toLowerCase();
    if (s === "open" && Number(inv.emailed_count ?? 0) > 0) return "sent";
    return statusWord(INVOICE_STATUS_WORD, inv.status);
}

/**
 * pass → passed, fail → failed, an old record nobody has read → result not
 * known, anything else (couldn't test, not finished) → couldn't test. Reading
 * every non-fail as a pass told the office a locked-out DC passed (job
 * #092678160, 2026-10-06). One rule: lib/test-state.ts.
 */
export function storyOutcome(overallResult: string | null | undefined): StoryOutcome {
    const s = testState(overallResult);
    if (s === "pass") return "passed";
    if (s === "fail") return "failed";
    if (s === "unknown") return "not_known";
    return "not_tested";
}

export const OUTCOME_MARK: Record<StoryOutcome, string> = { passed: "✓", failed: "✗", not_tested: "–", not_known: "?" };
export const OUTCOME_WORD: Record<StoryOutcome, string> = {
    passed: "passed",
    failed: "failed",
    not_tested: "couldn't test",
    not_known: "result not known",
};

const VISIT_UNDERWAY = new Set(["en_route", "arrived", "in_progress"]);

// ── The derivation ─────────────────────────────────────────────────────────

export function deriveJobStory(
    job: StoryJobLike,
    testResults: StoryTestLike[] = [],
    invoices: StoryInvoiceLike[] = [],
    estimates: StoryEstimateLike[] = [],
    deliveredAnotherWay: StoryOtherWayLike | null = null,
): JobStory {
    const delivery = deliveredAnotherWay ? otherWayStory(deliveredAnotherWay) : null;
    // Latest result per device, kept in first-seen order — a retest replaces
    // the earlier beat rather than adding a second one.
    const latestByAsset = new Map<string, StoryTestLike>();
    for (const t of testResults) {
        const prev = latestByAsset.get(t.asset_id);
        if (!prev || new Date(t.performed_at).getTime() >= new Date(prev.performed_at).getTime()) {
            latestByAsset.set(t.asset_id, t);
        }
    }
    const latest = Array.from(latestByAsset.values());
    const happened = latest.length > 0;

    // Money beats exist independent of the visit (a lineage estimate can
    // pre-date it), so derive them first.
    const money: StoryMoney[] = [];
    for (const inv of invoices) {
        if (DEAD_MONEY_STATUSES.has((inv.status ?? "").toLowerCase())) continue;
        money.push({
            kind: "invoice",
            id: inv.id,
            label: "Invoice",
            amount: fmtMoney(inv.total_amount),
            status: invoiceStatusWord(inv),
        });
    }
    for (const est of estimates) {
        if (DEAD_MONEY_STATUSES.has((est.status ?? "").toLowerCase())) continue;
        money.push({
            kind: "estimate",
            id: est.id,
            label: est.display_number ? `Estimate #${est.display_number}` : "Estimate",
            amount: fmtMoney(est.subtotal),
            status: statusWord(ESTIMATE_STATUS_WORD, est.status),
        });
    }

    if (!happened) {
        const when = fmtDateTime(job.scheduled_start);
        const tech = job.assigned_to_name?.trim() || null;
        const preVisitLine = VISIT_UNDERWAY.has(job.status)
            ? "Visit underway — results land here as the tech submits."
            : when
              ? `Nothing yet — visit is ${when}${tech ? ` with ${tech}` : ""}.`
              : "Nothing yet — the visit hasn't been scheduled.";
        return { happened: false, head: null, beats: [], money, line: null, preVisitLine };
    }

    // Sort chronologically so the story reads in the order it happened.
    latest.sort((a, b) => new Date(a.performed_at).getTime() - new Date(b.performed_at).getTime());

    const beats: StoryBeat[] = latest.map((t) => {
        const outcome = storyOutcome(t.overall_result);
        const failed = outcome === "failed";
        const firstReason = (t.failure_reasons ?? []).find((r) => !!r && r.trim().length > 0) ?? null;
        return {
            assetId: t.asset_id,
            label: deviceLabel(t),
            passed: outcome === "passed",
            outcome,
            reason: failed && firstReason ? shortReason(firstReason) : null,
        };
    });

    const last = latest[latest.length - 1];
    const when = fmtDay(last.performed_at);
    const tech = last.performed_by_name || last.tester_name || job.assigned_to_name || null;
    const head = [when, tech].filter(Boolean).join(" · ");

    const beatsText = beats
        .map(
            (b) =>
                `${OUTCOME_MARK[b.outcome]} ${b.label} ${OUTCOME_WORD[b.outcome]}${b.reason ? ` (${b.reason})` : ""}`,
        )
        .join(" · ");
    const moneyBits = money.map((m) =>
        `${m.amount ? `${m.amount} ` : ""}${m.kind === "invoice" ? "invoice" : "quote"} ${m.status}`.trim(),
    );
    const line = composeStoryLine([head, beatsText], delivery ? [...moneyBits, delivery] : moneyBits);

    return { happened: true, head: head || null, beats, money, line, preVisitLine: null, delivery };
}

/**
 * The Money zone's three honest numbers. Balance = everything not yet
 * collected: what's still open on live invoices plus the value of quotes the
 * customer hasn't said yes to (the mockup's $2,512 = $172 + $2,340).
 */
export function deriveMoneyTotals(invoices: StoryInvoiceLike[] = [], estimates: StoryEstimateLike[] = []): MoneyTotals {
    let invoiceTotal = 0;
    let invoiceOutstanding = 0;
    for (const inv of invoices) {
        const status = (inv.status ?? "").toLowerCase();
        if (DEAD_MONEY_STATUSES.has(status)) continue;
        const total = Number(inv.total_amount ?? 0);
        if (Number.isFinite(total)) invoiceTotal += total;
        if (status === "paid") continue;
        let outstanding: number;
        if (inv.st_balance != null && Number.isFinite(Number(inv.st_balance))) {
            outstanding = Number(inv.st_balance);
        } else {
            const paid = Number(inv.payments_total ?? 0);
            outstanding = (Number.isFinite(total) ? total : 0) - (Number.isFinite(paid) ? paid : 0);
        }
        invoiceOutstanding += Math.max(0, outstanding);
    }

    let estimateAwaiting = 0;
    for (const est of estimates) {
        const status = (est.status ?? "").toLowerCase();
        if (status !== "draft" && status !== "sent") continue;
        const sub = Number(est.subtotal ?? 0);
        if (Number.isFinite(sub)) estimateAwaiting += sub;
    }

    return {
        invoiceTotal,
        invoiceOutstanding,
        estimateAwaiting,
        balance: invoiceOutstanding + estimateAwaiting,
    };
}

/** "$2,512" formatting for the Money zone (whole dollars — glance numbers). */
export function formatMoneyStat(n: number): string {
    return `$${Math.round(n).toLocaleString("en-US")}`;
}
